import { loadNotificationSettings, roomNotificationSetting, type SoundId } from "./settings";
let ctx: AudioContext | null = null;
let unlockInstalled = false;
const messageBuffers = new Map<SoundId, Promise<AudioBuffer>>();
const SOUND_ASSET_VERSION = "20260915-aosp1";
export function audioContext(): AudioContext | null { try { return ctx ??= new AudioContext(); } catch { return null; } }
/**
 * РАЗБЛОКИРОВЩИК ЗВУКА. Снимаем слушатели только когда контекст ДЕЙСТВИТЕЛЬНО
 * работает (замер 2026-09-21).
 *
 * Было: первый же жест снимал оба слушателя БЕЗУСЛОВНО — даже если resume()
 * отклонился или контекст снова уснул позже (вкладка/WebView в фоне, Android
 * забрал аудиофокус). Второго шанса не было до перезагрузки страницы, и все
 * последующие звуки — в том числе гудок исходящего вызова, который стартует
 * из эффекта уже ПОСЛЕ сетевого join(), далеко от жеста, — молчали.
 * Стало: слушатели живут, пока контекст не в состоянии "running", и любой
 * следующий жест человека даёт ещё одну попытку. Слушатели пассивные и
 * снимаются сразу после успеха, так что постоянной нагрузки нет.
 */
export function installSoundUnlock(): void {
  if (unlockInstalled) return; unlockInstalled = true;
  const unlock = () => {
    const ac = audioContext();
    if (!ac) return;
    if (ac.state === "running") { detach(); return; }
    void ac.resume().then(() => { if (ac.state === "running") detach(); }).catch(() => {
      /* Оставляем слушатели на месте: следующий жест — ещё одна попытка. */
    });
  };
  const detach = () => {
    window.removeEventListener("pointerdown", unlock);
    window.removeEventListener("keydown", unlock);
  };
  window.addEventListener("pointerdown", unlock, { passive: true });
  window.addEventListener("keydown", unlock);
}
function loadMessageBuffer(ac: AudioContext, id: SoundId): Promise<AudioBuffer> {
  let pending = messageBuffers.get(id);
  if (!pending) {
    pending = fetch(`/sounds/message-${id}.mp3?v=${SOUND_ASSET_VERSION}`, { cache: "no-cache" })
      .then((response) => {
        if (!response.ok) throw new Error(`message sound HTTP ${response.status}`);
        return response.arrayBuffer();
      })
      .then((bytes) => ac.decodeAudioData(bytes));
    messageBuffers.set(id, pending);
  }
  return pending;
}
export function playTone(id: SoundId = loadNotificationSettings().messageSound): void {
  const ac = audioContext(); if (!ac) return;
  const play = async () => {
    try {
      const source = ac.createBufferSource();
      source.buffer = await loadMessageBuffer(ac, id);
      source.connect(ac.destination);
      source.start();
    } catch {
      // Audio is best effort; notification display remains independent.
    }
  };
  if (ac.state === "suspended") void ac.resume().then(() => play()).catch(() => {}); else void play();
}
export function playIncomingSound(): void { playTone(loadNotificationSettings().messageSound); }
/** Use the room override when present, otherwise the global message sound. */
export function playIncomingSoundForRoom(roomId: string): void {
  playTone(roomNotificationSetting(roomId)?.sound ?? loadNotificationSettings().messageSound);
}
export function previewSound(id: SoundId): void { playTone(id); }
